import React from 'react';

/**
 * Simple markdown renderer component
 * Handles basic markdown formatting without heavy dependencies
 */
const MarkdownRenderer = ({ content }) => {
  if (!content) return null;

  const renderLine = (line, index) => {
    // Handle headers
    if (line.startsWith('#### ')) {
      return <h6 key={index} className="mt-3 mb-2">{line.substring(5)}</h6>;
    }
    if (line.startsWith('### ')) {
      return <h5 key={index} className="mt-3 mb-2">{line.substring(4)}</h5>;
    }
    if (line.startsWith('## ')) {
      return <h4 key={index} className="mt-3 mb-2">{line.substring(3)}</h4>;
    }
    if (line.startsWith('# ')) {
      return <h3 key={index} className="mt-3 mb-2">{line.substring(2)}</h3>;
    }

    // Handle bullet points
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      return (
        <li key={index} className="mb-1">
          {renderInlineFormatting(line.trim().substring(2))}
        </li>
      );
    }

    // Handle numbered lists
    if (/^\d+\.\s/.test(line.trim())) {
      const match = line.trim().match(/^\d+\.\s(.+)$/);
      if (match) {
        return (
          <li key={index} className="mb-1">
            {renderInlineFormatting(match[1])}
          </li>
        );
      }
    }

    // Handle empty lines
    if (!line.trim()) {
      return <br key={index} />;
    }

    // Regular paragraph
    return (
      <p key={index} className="mb-2">
        {renderInlineFormatting(line)}
      </p>
    );
  };

  const renderInlineFormatting = (text) => {
    if (!text) return null;

    // Split by bold markers
    const parts = [];
    let currentText = text;
    let key = 0;

    // Handle bold (**text**)
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>
            {renderItalic(text.substring(lastIndex, match.index))}
          </span>
        );
      }
      // Add bold text
      parts.push(
        <strong key={`bold-${key++}`}>
          {renderItalic(match[1])}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${key++}`}>
          {renderItalic(text.substring(lastIndex))}
        </span>
      );
    }

    return parts.length > 0 ? parts : text;
  };

  const renderItalic = (text) => {
    if (!text) return null;

    const parts = [];
    const italicRegex = /\*(.+?)\*/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = italicRegex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add italic text
      parts.push(
        <em key={`italic-${key++}`}>{match[1]}</em>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Process content line by line
  const lines = content.split('\n');
  const rendered = [];
  let listItems = [];
  let listType = null; // 'ul' or 'ol'

  lines.forEach((line, index) => {
    const isBullet = line.trim().startsWith('- ') || line.trim().startsWith('* ');
    const isNumbered = /^\d+\.\s/.test(line.trim());

    if (isBullet) {
      if (listType !== 'ul') {
        // Close previous list if different type
        if (listItems.length > 0) {
          rendered.push(
            listType === 'ol' 
              ? <ol key={`list-${index}`} className="mb-3">{listItems}</ol>
              : <ul key={`list-${index}`} className="mb-3">{listItems}</ul>
          );
          listItems = [];
        }
        listType = 'ul';
      }
      listItems.push(renderLine(line, `li-${index}`));
    } else if (isNumbered) {
      if (listType !== 'ol') {
        // Close previous list if different type
        if (listItems.length > 0) {
          rendered.push(
            listType === 'ol'
              ? <ol key={`list-${index}`} className="mb-3">{listItems}</ol>
              : <ul key={`list-${index}`} className="mb-3">{listItems}</ul>
          );
          listItems = [];
        }
        listType = 'ol';
      }
      listItems.push(renderLine(line, `li-${index}`));
    } else {
      // Close any open list
      if (listItems.length > 0) {
        rendered.push(
          listType === 'ol'
            ? <ol key={`list-${index}`} className="mb-3">{listItems}</ol>
            : <ul key={`list-${index}`} className="mb-3">{listItems}</ul>
        );
        listItems = [];
        listType = null;
      }
      rendered.push(renderLine(line, index));
    }
  });

  // Close any remaining list
  if (listItems.length > 0) {
    rendered.push(
      listType === 'ol'
        ? <ol key="list-final" className="mb-3">{listItems}</ol>
        : <ul key="list-final" className="mb-3">{listItems}</ul>
    );
  }

  return <div className="markdown-renderer">{rendered}</div>;
};

export default MarkdownRenderer;
